import { computed, ref } from 'vue';

import {
  ApiError,
  api,
  type CheckoutResult,
  type FuelRefillKey,
  type GarageCustomise,
  type GarageOverview,
} from '@/net/api';
import { useSession } from './session';
import { showNotice } from './notification-banner';
import { describeOutcome } from './garage-outcome';
import { NO_TITLE } from '@shared/titles';
import { sortTitlesForShop } from '@/garage/title-order';
import { formatCredits } from '@/garage/stat-display';
import { usePayments } from './payments';

/**
 * The garage, as the screen sees it.
 *
 * Every mutation replaces the whole overview with the server's answer rather
 * than patching the local copy. A purchase moves the balance, the ownership
 * flags, the affordability of everything else and the resolved spec all at
 * once, and re-deriving that here is how a shop starts showing prices it will
 * not honour. Buying diamonds and boosts goes through the same door for the
 * same reason.
 *
 * `loadedAtMs` exists because two fields in the overview age: the server derives
 * fuel from a stored reading and a timestamp, so `wallet.secondsToNextFuel` is a
 * remainder measured at the instant of the response, and a research in progress
 * is the same shape. The screen counts down from them, and when one runs out it
 * re-reads rather than incrementing a local number or concluding on its own that
 * the research finished — see `garage/format.ts`.
 */
const overview = ref<GarageOverview | null>(null);
const loading = ref(false);
const busyId = ref<string | null>(null);
const error = ref<string | null>(null);
/** When the current overview was received. The countdown measures from here. */
const loadedAtMs = ref(0);

/**
 * The hull whose customise window is open, or null for none.
 *
 * Held here rather than in the screen because it has to survive a mutation: a
 * part bought from inside the window replaces the whole overview, and the
 * window has to be rebuilt from the server's new answer rather than left
 * showing the prices and deltas from before the purchase.
 */
const customiseOpen = ref<GarageCustomise | null>(null);

const FALLBACK_PAINT = { primary: '#4b5320', secondary: '#2f3517' };
/** So a day with nothing in it renders as an empty box rather than as an error. */
const NO_QUESTS = {
  dayIndex: 0,
  secondsUntilReset: 0,
  quests: [],
  unclaimed: 0,
  dayReward: { credits: 0, xp: 0 },
  rerollCost: 0,
  rerollable: false,
  rerolls: 0,
};

/**
 * Dev preview only (`/win`, `/defeat`): there is no session to fetch a garage
 * from, so the result screen's fuel card would never have a wallet to draw.
 * A partial overview, cast — the preview reads nothing else from it. It is
 * sticky: a signed-in browser would otherwise refresh over it with a real,
 * usually full, tank and the card would vanish.
 */
let previewSeeded = false;
export function seedPreviewGarage(): void {
  previewSeeded = true;
  overview.value = {
    wallet: { fuel: 2, fuelCapacity: 5, diamonds: 500 },
    shop: {
      boosts: [],
      fuelRefill: {
        capacity: 5,
        tiers: [
          { units: 1, diamondCost: 10, available: true },
          { units: 'full', diamondCost: 27, available: true },
        ],
      },
    },
  } as unknown as GarageOverview;
}

export function useGarage() {
  return {
    overview: computed(() => overview.value),
    loading: computed(() => loading.value),
    busyId: computed(() => busyId.value),
    error: computed(() => error.value),

    credits: computed(() => overview.value?.player.credits ?? 0),
    /**
     * The livery fitted right now, for the showroom.
     *
     * Exposed as its own computed rather than read off `overview` at the call
     * site, so the hangar and the shop cards cannot disagree about what is
     * fitted — the same reason the equipped hull and turret are here.
     */
    equippedPaintId: computed(() => overview.value?.player.equippedPaintId ?? null),
    diamonds: computed(() => overview.value?.wallet.diamonds ?? 0),
    wallet: computed(() => overview.value?.wallet ?? null),
    shop: computed(() => overview.value?.shop ?? null),
    /**
     * Empty until the overview lands, like `ranks` and for the same reason: a
     * shop that invented three cards before it knew their prices would show a
     * price it will not honour, however briefly.
     */
    bundles: computed(() => overview.value?.bundles ?? []),
    loadedAtMs: computed(() => loadedAtMs.value),

    hulls: computed(() => itemsOfKind('HULL')),
    turrets: computed(() => itemsOfKind('TURRET')),
    paints: computed(() => itemsOfKind('PAINT')),
    titles: computed(() => sortTitlesForShop(itemsOfKind('TITLE'))),
    research: computed(() => overview.value?.research ?? []),
    /**
     * The rank ladder, straight from the server.
     *
     * Empty until the overview lands, so the window that draws it renders
     * nothing rather than an invented twenty rows — the same reason the title
     * bar holds the credit balance back until it knows one.
     */
    ranks: computed(() => overview.value?.ranks ?? []),
    /**
     * Today's quests. Empty until the overview lands, like `ranks` and for the
     * same reason: a box that invented four rows before it knew their targets
     * would be showing a quest the server has not dealt.
     */
    quests: computed(() => overview.value?.quests ?? NO_QUESTS),
    /**
     * Which missions this account has beaten, and on which setting.
     *
     * Empty until the overview lands, like `ranks` and for the same reason: a
     * card badged COMPLETE before the server has said so is a claim about
     * somebody's history, and the honest thing to draw while the answer is
     * unknown is nothing.
     */
    missions: computed(() => overview.value?.missions ?? []),
    spec: computed(() => overview.value?.spec ?? null),
    customise: computed(() => overview.value?.customise ?? null),
    /** The window currently open, for whichever hull it was opened on. */
    customiseOpen: computed(() => customiseOpen.value),
    openCustomise,
    closeCustomise,
    paint: computed(() => overview.value?.paint ?? FALLBACK_PAINT),
    equippedHull: computed(() => findEquipped('HULL')),
    equippedTurret: computed(() => findEquipped('TURRET')),

    load,
    loadAfterSortie,
    refresh,
    claimQuest,
    rerollQuests,
    purchase: (id: string) => mutate(id, () => api.purchase(id), purchaseNotice(id)),
    upgrade: (id: string) => mutate(id, () => api.upgrade(id), 'Upgrade started'),
    installUpgrade: (id: string) => mutate(id, () => api.installUpgrade(id), 'Upgrade installed'),
    skipUpgrade: (id: string) => mutate(id, () => api.skipUpgrade(id), 'Upgrade installed'),
    prestige: (id: string) => mutate(id, () => api.prestige(id), 'Top-tier fitted'),
    research_: (id: string) => mutate(id, () => api.research(id), 'Research started'),
    skipResearch: (id: string) => mutate(id, () => api.skipResearch(id), 'Research finished'),
    equipHull: (id: string) => mutate(id, () => api.equip({ hullId: id }), 'Hull fitted'),
    equipTurret: (id: string) => mutate(id, () => api.equip({ turretId: id }), 'Turret fitted'),
    equipPaint: (id: string) => mutate(id, () => api.equip({ paintId: id }), 'Livery applied'),
    equipTitle: (id: string) => mutate(id, () => api.equip({ titleId: id }), 'Title worn'),
    /**
     * Drives a saved bay: the same two equips, without the two banners.
     *
     * A bay is the one control in the garage that fires two mutations from one
     * click, and it was announcing both — `Hull fitted` and then `Turret fitted`
     * a moment later, on every switch, for a click whose whole purpose is to be
     * quick. The bay itself lights up as the confirmation, which is a better one
     * than a strip under the title bar: it is where the player is already
     * looking.
     *
     * Sequential rather than parallel because `mutate` refuses while another is
     * in flight, and skipping the half that is already fitted keeps a switch that
     * shares a hull down to one call. A refusal — a bay naming a turret the
     * account no longer owns — still lands in `error`, which the garage draws as
     * its own strip above the floor; going quiet drops the confirmation, never
     * the complaint.
     */
    driveBay: async (hullId: string, turretId: string) => {
      if (hullId !== overview.value?.player.equippedHullId) {
        await mutate(hullId, () => api.equip({ hullId }), null);
      }
      if (
        error.value === null &&
        turretId !== overview.value?.player.equippedTurretId
      ) {
        await mutate(turretId, () => api.equip({ turretId }), null);
      }
    },
    /**
     * Takes the title off.
     *
     * `NO_TITLE` rather than an `unequip` route: the server resolves the
     * sentinel in the one slot that accepts it, so this is the same call the
     * line above makes and goes through the same ownership-checked path. The
     * busy id is the sentinel, which is fine — nothing on the page keys a
     * spinner off it, and the control disables on `busyId` being set at all.
     */
    removeTitle: () =>
      mutate(NO_TITLE, () => api.equip({ titleId: NO_TITLE }), 'Title removed'),

    /**
     * Both part calls name a hull, and for the same reason: a part is owned by
     * one chassis, so neither buying nor fitting is a statement about the
     * account. The busy id stays the *part* — it is what the window disables a
     * button on, and a window is only ever open on one hull.
     */
    buyPart: (hullId: string, partId: string) =>
      mutate(partId, () => api.buyPart(hullId, partId), 'Part bought'),
    equipPart: (hullId: string, partId: string) =>
      mutate(partId, () => api.equipPart(hullId, partId), 'Part fitted'),

    /**
     * Diamond packs and the custom amount go through core payments with the
     * method chosen in the shop (`state/payments.ts`) and the player's currency
     * (the picker asks the player to confirm a currency switch for methods that
     * cannot charge in it).
     */
    buyDiamonds: (id: string) => {
      const { method, currency } = paymentChoice();
      return checkout(id, () => api.buyDiamonds(id, method, currency));
    },
    /**
     * Diamonds to credits. Keyed on the amount so two different conversions are
     * two mutations rather than one that overwrites the other's in-flight state.
     */
    convertDiamonds: (diamonds: number) =>
      mutate(
        `convert_${diamonds}`,
        () => api.convertDiamonds(diamonds),
        'Converted to credits',
      ),
    /**
     * The notice is deliberately vague about *what* arrived.
     *
     * A bundle whose hull the player already owned grants only the turret, and
     * which half that was is the server's answer — the card behind the banner is
     * rebuilt from the same response and says it properly. A one-line notice
     * that guessed would be the one place in this flow inventing a fact.
     */
    buyBundle: (id: string, offerId?: string) =>
      mutate(id, () => api.buyBundle(id, offerId), 'Bundle issued'),
    buyBoost: (id: string) => mutate(id, () => api.buyBoost(id), 'Boost active'),
    /**
     * The Commission Office. Each answers with the whole overview like every
     * other mutation, so the balance and the inventory are right on return; the
     * office's own lists are re-read by `state/market.ts` afterwards.
     */
    marketList: (body: Parameters<typeof api.marketList>[0]) =>
      mutate(`market_list_${body.itemId}`, () => api.marketList(body), 'Listed for sale'),
    marketCancel: (listingId: string) =>
      mutate(`market_cancel_${listingId}`, () => api.marketCancel(listingId), 'Listing withdrawn'),
    marketBuy: (listingId: string) =>
      mutate(`market_buy_${listingId}`, () => api.marketBuy(listingId), 'Purchased'),
    /**
     * Instant fuel, at whichever tier the shop card asked for.
     *
     * Keyed on the units so two different tiers are two mutations rather than
     * one clobbering the other's in-flight state — the same reason `convertDiamonds`
     * keys on the amount.
     */
    refillFuel: (units: FuelRefillKey = 'full') =>
      mutate(`fuel_refill_${units}`, () => api.refillFuel(units), 'Tank refilled'),
    buyFuelTank: () => mutate('fuel_tank', () => api.buyFuelTank(), 'Fuel tank fitted'),
    /**
     * A custom diamond amount, priced by the shop's own rate. Keyed on the
     * amount for the same reason `convertDiamonds` is.
     */
    buyCustomPack: (diamonds: number) => {
      const { method, currency } = paymentChoice();
      const base = overview.value?.shop?.customPack?.baseRateCentsPer100;
      return checkout(`custom_pack_${diamonds}`, () => api.buyCustomPack(diamonds, method, currency, base));
    },
    /**
     * Experience for diamonds. Keyed on the amount like the two above, so two
     * different amounts in flight cannot clobber each other's busy state.
     *
     * The notice says what arrived rather than what it cost, and deliberately does
     * not mention a rank: a purchase may or may not promote somebody, the server's
     * own overview answers that in the title bar a moment later, and a banner that
     * guessed would be inventing the one fact the player most wants to be true.
     */
    buyXp: (diamonds: number) =>
      mutate(`buy_xp_${diamonds}`, () => api.buyXp(diamonds), 'Experience credited'),

    dismiss,
    reset,
  };
}

/**
 * What the banner says after a purchase.
 *
 * The server applies a bought paint or title in the same transaction as the grant
 * — see `AUTO_APPLY_SLOTS` in `garage.service.ts` — so a bare "Purchased" would
 * undersell what just happened, and *that* is the exact failure the auto-apply
 * exists to close: a player who does not notice the change assumes the purchase
 * did not work. The whole point is to say the change happened.
 *
 * Read off the overview **before** the call, because the kind is a fact about the
 * catalogue rather than about the response, and the response has not arrived.
 * Falls back to the plain word for anything the client has not got a card for.
 */
function purchaseNotice(id: string): string {
  const kind = overview.value?.items.find((item) => item.id === id)?.kind;
  if (kind === 'PAINT') return 'Purchased and applied';
  if (kind === 'TITLE') return 'Purchased and worn';
  return 'Purchased';
}

function itemsOfKind(kind: 'HULL' | 'TURRET' | 'PAINT' | 'TITLE') {
  return (overview.value?.items ?? []).filter((i) => i.kind === kind);
}

function findEquipped(kind: 'HULL' | 'TURRET') {
  const id =
    kind === 'HULL'
      ? overview.value?.player.equippedHullId
      : overview.value?.player.equippedTurretId;
  return (overview.value?.items ?? []).find((i) => i.id === id) ?? null;
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    apply(await api.garage());
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : 'Could not load the garage.';
  } finally {
    loading.value = false;
  }
}

/**
 * The garage load that happens on the way back from a battle.
 *
 * It used to also raise a "Battle report" popup off `lastSortie` — the receipt
 * for the payout banked on disconnect. That window predates the result screen:
 * while the payout was time-based, no honest figure could be shown before the
 * session closed, so the receipt had to chase the player into the garage. The
 * payout clock now stops at the deciding tick and `BattleSummary.vue` shows the
 * exact banked figures on the result screen itself, which made the popup a
 * second statement of the same payout — read as an *extra* reward, which it
 * never was. One battle, one payout, one place it is shown.
 *
 * Kept as its own entry point rather than folded into `load` because the retry
 * is still earning its keep for the *balances*: credits are paid when the
 * session closes on disconnect, and the browser fires this request in the same
 * breath as it asks the socket to close, so the two are racing. Without the
 * re-read, the title bar could show the pre-battle balance until something else
 * refreshed it.
 */
async function loadAfterSortie(): Promise<void> {
  await load();
  await new Promise((resolve) => setTimeout(resolve, SORTIE_BALANCE_RETRY_MS));
  await refresh();
}

/**
 * How long to wait before re-reading balances a closing session may not have
 * banked yet. One retry rather than a poll: if the payout still has not landed,
 * the next ordinary refresh will show it.
 */
const SORTIE_BALANCE_RETRY_MS = 900;

/**
 * Claims a finished daily quest and shows what it paid.
 *
 * Not routed through `mutate`: a claim can be made from the quests window
 * while the garage floor is still behind it, and `mutate` holds `busyId` for
 * the whole of its action, disabling every other control in the garage for as
 * long as a confirmation is on screen — which is the shape a *question* wants
 * (see `ConfirmPurchase.vue`) and this is not one. The busy flag is dropped the
 * moment the request returns; the notice is shown after and is deliberately
 * not awaited, because nothing here is waiting on it being read.
 *
 * The wording lives here rather than in the quests window or the daily-quest
 * card, exactly as the purchase confirmations live at `GarageScreen`'s
 * handlers rather than in each card: one call site is one wording, and two
 * would be two wordings a month from now — and it covers both callers, since
 * `DailyQuestBox` and `DailyQuestsWindow` both emit `claim` up to whichever
 * screen owns the store rather than claiming for themselves. The figures are
 * the *server's* — a claim answers with what it actually paid, not with what
 * the card was displaying.
 *
 * The confirmation is `ui/NotificationBanner.vue`, a non-blocking strip under
 * the title bar, rather than `ui/RewardPopup.vue`'s centred, focus-holding
 * scrim: a quest claim is a garage-floor event a player presses and keeps
 * working under, not a moment that needs the screen. See
 * `state/notification-banner.ts` for the reasoning in full.
 */
/**
 * Re-rolls the day's unfinished quests. The confirmation is the caller's
 * (`GarageScreen` asks first); this is only the request, and the server prices
 * and refuses it.
 */
async function rerollQuests(): Promise<void> {
  if (busyId.value) return;
  busyId.value = 'quest-reroll';
  error.value = null;
  try {
    const answer = await api.rerollQuests();
    apply(answer.overview);
    showNotice({ title: 'Daily quests re-rolled', lines: ['A new set of unfinished quests is on the board.'], autoDismissMs: 5000 });
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : 'That did not work.';
  } finally {
    busyId.value = null;
  }
}

async function claimQuest(questId: string): Promise<void> {
  if (busyId.value) return;
  busyId.value = questId;
  error.value = null;

  let reward: { questName: string; credits: number; xp: number } | null = null;
  try {
    const answer = await api.claimQuest(questId);
    apply(answer.overview);
    reward = answer.reward;
    // No `notice.value` here: the generic strip and the banner below would say
    // the same thing twice, in two different places, on the same click.
  } catch (err) {
    // The server's refusal is the message worth showing: "You have already
    // claimed that" tells the player what happened, "failed" does not.
    error.value = err instanceof ApiError ? err.message : 'That did not work.';
  } finally {
    busyId.value = null;
  }

  if (!reward) return;
  showNotice({
    title: 'Reward claimed',
    lines: [
      `${reward.questName} — +${formatCredits(reward.xp)} XP, ` +
        `+${formatCredits(reward.credits)} credits`,
    ],
    // Long enough to read a sentence twice; the close button covers the rest.
    autoDismissMs: 7000,
  });
}

/**
 * A silent re-read.
 *
 * Used when a countdown reaches zero: the amount of fuel, or whether a research
 * has landed, has changed on the server and the client has deliberately not
 * guessed at it. A failure here is
 * not worth an error banner — the previous overview is still on screen and the
 * next tick will try again.
 */
async function refresh(): Promise<void> {
  if (loading.value || busyId.value) return;
  try {
    apply(await api.garage());
  } catch {
    // Deliberately silent. See above.
  }
}

/**
 * Opens the customise window on one hull.
 *
 * The equipped hull's window already came down with the overview, so opening it
 * costs nothing; any other owned hull is a fetch, because parts are stored per
 * hull and pre-building all of them on every garage load would be seven eighths
 * wasted work. Ownership is the server's call either way — this only decides
 * whether to spend the round trip.
 */
async function openCustomise(hullId: string): Promise<void> {
  const equipped = overview.value?.customise ?? null;
  if (equipped && equipped.hullId === hullId) {
    customiseOpen.value = equipped;
    return;
  }
  error.value = null;
  try {
    customiseOpen.value = await api.customise(hullId);
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : 'Could not open that hull.';
  }
}

function closeCustomise(): void {
  customiseOpen.value = null;
}

/**
 * Rebuilds an open customise window against a freshly arrived overview.
 *
 * Every mutation answers with the whole overview, which carries the *equipped*
 * hull's window — so a window open on some other hull would otherwise keep
 * showing the fitting and the prices from before the change. Silent on failure:
 * the window is still on screen and still usable, and an error banner over a
 * successful purchase would be the wrong story.
 */
async function refreshCustomise(): Promise<void> {
  const open = customiseOpen.value;
  if (!open?.hullId) return;
  const equipped = overview.value?.customise ?? null;
  if (equipped && equipped.hullId === open.hullId) {
    customiseOpen.value = equipped;
    return;
  }
  try {
    customiseOpen.value = await api.customise(open.hullId);
  } catch {
    // Deliberately silent. See above.
  }
}

/**
 * Runs a mutation, replaces the overview with the answer, and says what happened.
 *
 * `success` is the *verb* — `Upgraded`, `Purchased and applied`, `Part fitted` —
 * and it is all the call site gets to choose. Everything under it is read off
 * the two overviews by `state/garage-outcome.ts`: what was acted on, what
 * arrived, what it cost, and which stats moved. That split is deliberate and is
 * the reason eighteen call sites did not have to learn anything: a spend knows
 * what it *asked for*, and only the pair of answers either side of it knows what
 * actually happened.
 *
 * **`null` for `success` means no banner at all.** It exists for the one caller
 * that repeats a mutation another control already confirmed — driving a saved
 * bay fires two equips in a row, and two banners for one click is a worse
 * confirmation than none. A failure on that path is still reported, through
 * `error` and the strip that draws it.
 */
async function mutate(
  id: string,
  action: () => Promise<GarageOverview>,
  success: string | null,
): Promise<void> {
  if (busyId.value) return;
  busyId.value = id;
  error.value = null;
  // Held across the call so the spend can be read off the difference. `apply`
  // overwrites `overview`, so this has to be captured first.
  const before = overview.value;
  try {
    const next = await action();
    // Worked out before `apply`, which is the only reason `before` was held: the
    // difference between the two answers is what the notice is made of.
    const outcome = describeOutcome(id, before, next);
    apply(next);
    await refreshCustomise();
    // The confirmation is the shared banner, not an inline strip — the same
    // door the daily-quest claim already uses, and for the same reason: a
    // spend made from any tab is a garage-floor event the player keeps
    // working under, not a moment that needs its own line of markup per
    // screen. `lines` is empty for a mutation with nothing to report at all,
    // which the banner draws as a title on its own.
    if (success !== null) {
      showNotice({
        title: success,
        lines: outcome.lines,
        stats: outcome.stats.length > 0 ? outcome.stats : undefined,
        // Longer when there are bars to read: a title and one line is a glance,
        // and six before/after rows is a paragraph.
        autoDismissMs: outcome.stats.length > 0 ? 8000 : 5000,
      });
    }
  } catch (err) {
    // The server's refusal is the message worth showing: "Light Frames must be
    // unlocked first" tells the player what to do next, "failed" does not.
    error.value = err instanceof ApiError ? err.message : 'That did not work.';
  } finally {
    busyId.value = null;
  }
}

function paymentChoice(): { method: string; currency: string } {
  const payments = usePayments();
  const current = overview.value?.shop?.pricing?.currency ?? 'EUR';
  // Always the currency the prices are shown in: a method that cannot charge in
  // it is refused by the server (CURRENCY_NOT_SUPPORTED) rather than silently
  // charged in another currency — the picker offers the switch up front.
  return { method: payments.selected.value?.id ?? 'card', currency: current };
}

async function checkout(
  id: string,
  action: () => Promise<CheckoutResult>,
): Promise<void> {
  if (busyId.value) return;
  busyId.value = id;
  error.value = null;
  try {
    const result = await action();
    if (result.url) {
      window.location.href = result.url;
    }
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : 'That did not work.';
  } finally {
    busyId.value = null;
  }
}

function apply(next: GarageOverview): void {
  if (previewSeeded) return;
  overview.value = next;
  loadedAtMs.value = Date.now();
  const session = useSession();
  session.setBalances(next.player.credits, next.wallet.diamonds);
  // The profile's call sign — what chat and battle stamp — not the template's
  // username the session started with. See `callSign` in `state/session.ts`.
  session.setNickname(next.player.nickname);
}

function dismiss(): void {
  error.value = null;
}

function reset(): void {
  overview.value = null;
  error.value = null;
  loadedAtMs.value = 0;
  customiseOpen.value = null;
}
